import Button from '@amzn/meridian/button';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import React, { useEffect } from 'react';
import useForm from 'src/hooks/useForm';
import { downloadTWPA, setIsTwpaModalOpen } from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { DownloadTWPAForm, TemporaryWorkPlacementEnum } from 'src/store/placementDetailsPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import GeneralInfo from './generalInfo/generalInfo';
import RadioFields from './radioFields/radioFields';

/**
 * TWPA Modal
 */
const TwpaModal = () => {
  const { featureFlags } = useAppSelector((state) => state.user);
  const { placementDetails, placementSite, isTwpaModalOpen } = useAppSelector((state) => state.placementDetailsPage);
  const dispatch = useAppDispatch();
  const jobModificationsEnabled = featureFlags.JOB_MODIFICATIONS && !!placementSite?.jobModificationsEnabled;

  // Setup Form Hook
  const {
    handleChange,
    handleChangeDynamicKey,
    validateForm,
    resetFormField,
    resetForm,
    errors: twpaFormErrors,
    formData: twpaFormData,
  } = useForm<DownloadTWPAForm>({
    validations: {
      department: {
        required: { value: true, message: 'Department is required' },
      },
      placementShifts: {
        required: { value: true, message: 'Placement shift(s) is required' },
      },
      reportDate: {
        required: { value: true, message: 'Report date is required' },
      },
      workPlacementProvide: {
        required: {
          value: true,
          message: 'Please select one',
        },
      },
    },
  });

  const getTwpaRadioOptions = () => {
    const radioOptions = [
      {
        value: TemporaryWorkPlacementEnum.MATCH_ROLE_WITH_MODIFICATIONS,
        label: (
          <>
            Restrictions have been matched within your current role. The following modifications will be made in order
            to accommodate your restrictions.
          </>
        ),
      },
      {
        value: TemporaryWorkPlacementEnum.MATCH_JOB,
        label: (
          <>
            Restrictions have been matched with a job transfer to a process path or temporary light duty role. The
            following modifications to the process path or temporary light duty role will be made in order to
            accommodate your restrictions.
          </>
        ),
      },
      {
        value: TemporaryWorkPlacementEnum.NO_MATCH,
        label: (
          <>
            There are no Temporary Work Placements that meet your restrictions available at this time. Associate is
            responsible for calling DLS to initiate a Leave of Absence.
          </>
        ),
      },
    ];

    return [
      ...radioOptions,
      ...(!twpaFormData.jobModifications?.length
        ? [
            {
              value: TemporaryWorkPlacementEnum.MATCH_ROLE_NO_MODIFICATIONS,
              label: (
                <>
                  Restrictions have been matched within your current role. No modifications to the process path are
                  necessary in order to accommodate your restrictions.
                </>
              ),
            },
          ]
        : []),
    ];
  };

  useEffect(() => {
    resetForm();

    const dedupedAppliedModifications = jobModificationsEnabled
      ? [
          ...new Set(
            placementDetails?.allocatedJobShifts?.flatMap((ajs) =>
              ajs.appliedModifications.map((am) => am.modification.name),
            ),
          ),
        ].join(', ')
      : '';

    handleChange({ jobModifications: dedupedAppliedModifications });
  }, [dispatch, isTwpaModalOpen, placementDetails, jobModificationsEnabled]);

  useEffect(() => {
    if (
      twpaFormData.jobModifications?.length &&
      twpaFormData.workPlacementProvide === TemporaryWorkPlacementEnum.MATCH_ROLE_NO_MODIFICATIONS
    )
      resetFormField('workPlacementProvide');
  }, [twpaFormData.jobModifications]);

  return (
    <Modal
      title="Temporary Work Placement Acknowledgement Letter"
      open={isTwpaModalOpen}
      onClose={() => dispatch(setIsTwpaModalOpen(false))}
      width="680px"
    >
      <GeneralInfo
        twpaFormData={twpaFormData}
        twpaFormErrors={twpaFormErrors}
        handleChange={handleChange}
        jobModificationsEnabled={jobModificationsEnabled}
      />
      <RadioFields
        twpaFormData={twpaFormData}
        twpaFormErrors={twpaFormErrors}
        textInfo={
          <>
            Select which <strong>Temporary Work Placement</strong> will be provided.
          </>
        }
        formDataKey="workPlacementProvide"
        radioOptions={getTwpaRadioOptions()}
        handleChange={handleChangeDynamicKey}
      />
      <Row widths={['fill', '244px']}>
        <div />
        <Row>
          <Button onClick={() => dispatch(setIsTwpaModalOpen(false))}>Cancel</Button>
          <Button onClick={() => (validateForm() ? dispatch(downloadTWPA(twpaFormData as DownloadTWPAForm)) : null)}>
            Generate PDF
          </Button>
        </Row>
      </Row>
    </Modal>
  );
};

// **** Export default **** //

export default TwpaModal;
